import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { pageTransition, formatDate } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import {
  cancelLeaveRequest,
  getBalances,
  listCustomLeaveTypes,
  listLeaveRequests,
  createLeaveRequest,
  recordLeaveCertification,
  reviewLeaveRequest,
} from "@/api/leave";
import { PageHeader } from "@/components/layout/PageHeader";
import { ExportCsvButton } from "@/components/shared/ExportCsvButton";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { CalendarDays, Plus, Check, X } from "lucide-react";
import type {
  CustomLeaveType,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
} from "@/types/leave";
import { LEAVE_TYPE_LABELS, STANDARD_LEAVE_TYPES } from "@/types/leave";
import type { PaginatedResponse } from "@/types/common";

export function TimeOffPage() {
  const { user } = useAuth();
  const { hasRole } = usePermissions();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("requests");
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [requests, setRequests] = useState<PaginatedResponse<LeaveRequest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showNewModal, setShowNewModal] = useState(false);

  // New request form
  //
  // ``leaveType`` holds either one of the four standard buckets
  // (PTO/PERSONAL/SICK/OTHER) OR the literal id of a custom type
  // prefixed with ``custom:`` so the dropdown can stay a flat
  // <select>. The submit handler unpacks it into the API payload.
  const [leaveType, setLeaveType] = useState<string>("PTO");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customLeaveTypes, setCustomLeaveTypes] = useState<CustomLeaveType[]>(
    [],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [bal, req, customs] = await Promise.all([
        user?.id ? getBalances(user.id).catch(() => []) : Promise.resolve([]),
        listLeaveRequests(page),
        // Custom types are best-effort — a missing endpoint must not
        // break the page; the standard four buckets still render.
        listCustomLeaveTypes(false).catch(() => [] as CustomLeaveType[]),
      ]);
      setBalances(bal);
      setRequests(req);
      setCustomLeaveTypes(customs);
    } catch {
      addToast("error", "Failed to load time off data");
    } finally {
      setIsLoading(false);
    }
  }, [page, user?.id, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Resolve the dropdown value into ``(leave_type, custom_id)``.
  //   - ``"PTO"`` / ``"PERSONAL"`` / ``"SICK"`` / ``"OTHER"`` →
  //     standard bucket, no custom id.
  //   - ``"custom:<uuid>"`` → custom type. We rollup to whichever
  //     standard bucket the admin picked when creating the type.
  const resolveLeaveType = (
    val: string,
  ): { leave_type: LeaveType; custom_leave_type_id: string | null; requires_reason: boolean } => {
    if (val.startsWith("custom:")) {
      const id = val.slice("custom:".length);
      const ct = customLeaveTypes.find((c) => c.id === id);
      return {
        leave_type: ct?.rollup_leave_type ?? "OTHER",
        custom_leave_type_id: id,
        requires_reason: ct?.requires_reason ?? false,
      };
    }
    return {
      leave_type: val as LeaveType,
      custom_leave_type_id: null,
      // OTHER always asks for a reason; the standard buckets
      // (Vacation/Personal/Sick) don't.
      requires_reason: val === "OTHER",
    };
  };

  const resolved = resolveLeaveType(leaveType);
  const reasonRequired = resolved.requires_reason;

  // Match the user's selected type to their balance row for inline
  // "X days remaining" copy. We render it under the type dropdown
  // so the cap is visible BEFORE the user fills out dates and
  // hits submit just to be rejected by the 422.
  const matchingBalance = balances.find(
    (b) => b.leave_type === resolved.leave_type,
  );
  const remainingDays = matchingBalance
    ? matchingBalance.remaining_days
    : null;

  const handleSubmitRequest = async () => {
    if (!startDate || !endDate) return;
    if (reasonRequired && !reason.trim()) {
      addToast("error", "Please add a reason for this request");
      return;
    }
    setIsSubmitting(true);
    try {
      await createLeaveRequest({
        leave_type: resolved.leave_type,
        custom_leave_type_id: resolved.custom_leave_type_id,
        start_date: startDate,
        end_date: endDate,
        reason: reason.trim() || undefined,
        notes: notes || undefined,
      });
      addToast("success", "Time off request submitted");
      setShowNewModal(false);
      setStartDate("");
      setEndDate("");
      setReason("");
      setNotes("");
      fetchData();
    } catch (err: unknown) {
      // Surface the backend's policy / balance gate message verbatim
      // (422 includes a user-facing reason). Generic fallback only
      // kicks in for transport / 5xx errors.
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Failed to submit request",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (requestId: string) => {
    if (!confirm("Cancel this pending leave request?")) return;
    try {
      await cancelLeaveRequest(requestId);
      addToast("success", "Leave request cancelled");
      fetchData();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't cancel that request",
      );
    }
  };

  const handleReview = async (requestId: string, status: "APPROVED" | "DENIED") => {
    try {
      await reviewLeaveRequest(requestId, status);
      addToast("success", `Request ${status.toLowerCase()}`);
      fetchData();
    } catch {
      addToast("error", "Failed to review request");
    }
  };

  /**
   * Record (or clear) the HCP-certification expiry date on a
   * protected leave. ``isoDate`` comes from the inline <input
   * type="date"> on the row — empty string clears.
   *
   * Idempotent at the API layer; we still re-fetch so the table
   * reflects the new ``reviewer_notes`` stamp the backend appends.
   */
  const handleCertChange = async (
    requestId: string,
    isoDate: string,
  ) => {
    try {
      // ``<input type="date">`` returns "YYYY-MM-DD". The backend
      // expects an ISO datetime — promote to midnight UTC so we
      // don't drop into yesterday for users east of UTC.
      const expiresAt = isoDate
        ? new Date(`${isoDate}T00:00:00Z`).toISOString()
        : null;
      await recordLeaveCertification(requestId, expiresAt);
      addToast(
        "success",
        isoDate
          ? "Certification expiry recorded"
          : "Certification expiry cleared",
      );
      fetchData();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail;
      addToast("error", detail || "Failed to record certification");
    }
  };

  const columns: Column<LeaveRequest>[] = [
    {
      key: "employee",
      header: "Employee",
      render: (r) => (
        <span className="font-medium text-text-primary">
          {r.employee_name || "—"}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (r) => (
        <span className="text-text-primary">
          {/* Custom-type rows show the org-defined name; standard
              types show the canonical label. */}
          {r.custom_leave_type_name || LEAVE_TYPE_LABELS[r.leave_type]}
        </span>
      ),
    },
    {
      key: "dates",
      header: "Dates",
      render: (r) => (
        <span className="text-text-muted text-xs">
          {formatDate(r.start_date, "MMM d")} — {formatDate(r.end_date, "MMM d, yyyy")}
        </span>
      ),
    },
    {
      key: "days",
      header: "Days",
      render: (r) => <span className="text-text-primary">{r.total_days}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge
          variant={
            r.status === "APPROVED" ? "success" :
            r.status === "DENIED" ? "danger" :
            r.status === "PENDING" ? "warning" : "default"
          }
        >
          {r.status}
        </Badge>
      ),
    },
    {
      // Cert-expiry inline editor for protected (FMLA) leaves.
      // Non-protected rows show a dash. HR admins / managers get
      // the editable date input; everyone else sees the value as
      // plain text. Empty string clears the stored date.
      key: "cert_expires",
      header: "Cert expires",
      render: (r: LeaveRequest) => {
        const isProtected = r.leave_type === "FMLA";
        if (!isProtected) return <span className="text-text-muted">—</span>;
        const canEdit = hasRole("MANAGER", "HR_ADMIN", "SUPER_ADMIN");
        const value = r.certification_expires_at
          ? r.certification_expires_at.slice(0, 10)
          : "";
        if (!canEdit) {
          return (
            <span className="text-sm text-text-primary">
              {value || <span className="text-text-muted">Not recorded</span>}
            </span>
          );
        }
        return (
          <input
            type="date"
            defaultValue={value}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleCertChange(r.id, e.target.value)}
            className={
              "w-36 rounded-md border border-border bg-surface px-2 py-1 " +
              "text-sm text-text-primary focus:outline-none " +
              "focus:ring-2 focus:ring-accent/40"
            }
            title={
              value
                ? "HCP certification expiry. Empty to clear."
                : "Record the HCP certification expiry date."
            }
          />
        );
      },
    } as Column<LeaveRequest>,
    {
      key: "actions",
      header: "",
      render: (r: LeaveRequest) => {
        if (r.status !== "PENDING") return null;
        const isMine = !!user?.employee_id && r.employee_id === user.employee_id;
        const canReview = hasRole("MANAGER", "HR_ADMIN", "SUPER_ADMIN");
        return (
          <div className="flex gap-1">
            {/* Approve / Deny — managers + HR only, never on their own row.
                The backend self-approval guard re-enforces this so the SPA
                check is purely cosmetic. */}
            {canReview && !isMine && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReview(r.id, "APPROVED");
                  }}
                  className="p-1 rounded text-success hover:bg-success/10"
                  title="Approve"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReview(r.id, "DENIED");
                  }}
                  className="p-1 rounded text-danger hover:bg-danger/10"
                  title="Deny"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            )}
            {/* Cancel — only the requester themselves can cancel a
                pending row. Backend enforces the same; this hides the
                affordance from anyone who'd just hit a 404 on click. */}
            {isMine && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel(r.id);
                }}
                className="p-1 rounded text-text-muted hover:bg-surface-hover"
                title="Cancel request"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      },
    } as Column<LeaveRequest>,
  ];

  const tabs = [
    { id: "requests", label: "Requests", count: requests?.total },
    { id: "balances", label: "My Balances" },
    { id: "calendar", label: "Calendar" },
  ];

  // Build the request-modal dropdown — only the four standard
  // buckets PLUS any active org-defined custom types. Older legacy
  // values (BEREAVEMENT/FMLA/etc.) stay in the type system for
  // historical data display but are deliberately hidden from the
  // request UI per product policy.
  const leaveTypeOptions = [
    ...STANDARD_LEAVE_TYPES.map((v) => ({
      value: v as string,
      label: LEAVE_TYPE_LABELS[v],
    })),
    ...customLeaveTypes.map((c) => ({
      value: `custom:${c.id}`,
      label: c.name,
    })),
  ];

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Time Off"
        subtitle="Manage leave requests and balances"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/leave/calendar")}
            >
              <CalendarDays className="h-4 w-4" />
              Team calendar
            </Button>
            <ExportCsvButton resource="leave-requests" />
            <Button onClick={() => setShowNewModal(true)}>
              <Plus className="h-4 w-4" />
              Request Time Off
            </Button>
          </div>
        }
      />

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

      {activeTab === "requests" && (
        <div className="bg-surface rounded-xl border border-border">
          <DataTable
            columns={columns}
            data={requests?.items ?? []}
            keyExtractor={(r) => r.id}
            isLoading={isLoading}
            page={page}
            totalPages={requests?.total_pages ?? 1}
            onPageChange={setPage}
            emptyState={
              <EmptyState
                icon={CalendarDays}
                title="No leave requests yet"
                description="Plan some time off — a long weekend, a full vacation, whatever. Submitted requests show up here so you can track approvals and balances at a glance."
                action={{
                  label: "Request Time Off",
                  onClick: () => setShowNewModal(true),
                }}
              />
            }
          />
        </div>
      )}

      {activeTab === "balances" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {balances.length > 0 ? (
            balances.map((b) => (
              <Card key={b.id}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-text-primary">
                    {LEAVE_TYPE_LABELS[b.leave_type]}
                  </h4>
                  <CalendarDays className="h-5 w-5 text-accent" />
                </div>
                <p className="text-3xl font-bold font-heading text-text-primary">
                  {b.remaining_days}
                </p>
                <p className="text-xs text-text-muted mt-1">days remaining</p>
                <div className="mt-3 h-2 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, ((b.used_days + b.pending_days) / Math.max(b.total_days, 1)) * 100)}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-text-muted mt-1">
                  <span>{b.used_days} used</span>
                  <span>{b.pending_days} pending</span>
                  <span>{b.total_days} total</span>
                </div>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-text-muted">
              <p>No leave balances found. Submit a request to initialize your balances.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "calendar" && (
        <Card>
          <div className="text-center py-12 text-text-muted">
            <CalendarDays className="h-12 w-12 mx-auto mb-3 text-accent/50" />
            <p>View the full team calendar, holidays, and daily availability.</p>
            <Button
              className="mt-4"
              onClick={() => navigate("/leave/calendar")}
            >
              Open team calendar
            </Button>
          </div>
        </Card>
      )}

      {/* New Request Modal */}
      <Modal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        title="Request Time Off"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Select
              label="Leave Type"
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              options={leaveTypeOptions}
            />
            {remainingDays !== null && (
              <p
                className={`mt-1 text-xs ${
                  remainingDays > 0 ? "text-text-muted" : "text-warning"
                }`}
              >
                {remainingDays > 0
                  ? `You have ${remainingDays % 1 === 0 ? remainingDays : remainingDays.toFixed(1)} day${remainingDays === 1 ? "" : "s"} remaining this year.`
                  : "No days remaining this year — submitting will be blocked unless your admin allows over-drafts."}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {/* Reason — required for "Other" and any custom type the
              admin marked ``requires_reason``. Always rendered (never
              "appears suddenly") for less-jumpy form layout, but the
              label flips between Required / Optional. */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Reason{" "}
              {reasonRequired ? (
                <span className="text-danger">*</span>
              ) : (
                <span className="text-text-muted text-xs">(optional)</span>
              )}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={2}
              placeholder={
                reasonRequired
                  ? "Briefly explain why you need this time off…"
                  : "Optional — helps your manager approve faster"
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={2}
              placeholder="Any additional details for the approver…"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowNewModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitRequest} isLoading={isSubmitting}>
              Submit Request
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
